import { HttpErrorResponse, HttpParams, HttpResponse, HttpClient } from '@angular/common/http';
import { Inject, Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, share, shareReplay, take, tap } from 'rxjs/operators';
import { FilterDataRequest, FilterView, ToolbarFilterOption } from './filters-toolbar/filters-toolbar.model';
import { IUser } from './models';
import { EfficiencyTransport } from './models/efficiencies.models';
import { FloorClass } from './models/floor.class';
import { SpaceClass } from './models/space/space.class';
import { isTenantEntityActive } from './shared/utils/functions';
import { HttpMethods } from './shared/utils/http-methods.class';
import { Toolbar } from './stacking-plan-container/actions-toolbar/actions-toolbar.model';
import { Settings } from './stacking-plan-container/actions-toolbar/settings/settings.model';
import { IdentifiedIssue } from './stacking-plan-container/dialogs/pending-issues/pending-issues.model';
import { UpdateStackingPlanInfo, EDPFloorSpaceInfo } from './stacking-plan-container/floor-container/floor/floor.model';
import { OpportunitiesModel } from './stacking-plan-container/floor-container/floor/item/details-overlay/details-overlay.model';
import {
  FloorsElevators,
  MediaInfo,
  StackViewType,
  StackingPlanContainer,
} from './stacking-plan-container/stacking-plan.model';
import {
  CBREEmployee,
  SPDataStatus,
  EDPStackingPlan,
  LovValues,
  PropertyData,
  StackingPlanReviewer,
} from './stacking-plan.model';
import { STACKING_PLAN_CONFIGS_TOKEN, StackingPlanConfiguration } from './stacking-plan.token';
import { DataObject } from './models/data-object.models';
import { EDPSpaceInfo, SuiteType } from './stacking-plan-container/floor-container/floor/item/item.model';
import { Building } from '@miq/domain/pv';
import { FloorContainerRepository } from './stacking-plan-container/floor-container/floor-container-repository.service';
import { FloorContainer } from './stacking-plan-container/floor-container/floor-container.model';
import { CbreAlertService } from '@cbre/alerts';
import { GetEndPointSP } from './get-stacking-plan-endpoint.helper';
import { FiltersService } from './filters-toolbar/filters.service';
import { LocalizationOptionsService } from './localization-option.service';
import { NotificationService } from './shared/services/notification.service';
import { ConfirmDialogSPComponent } from './stacking-plan-container/dialogs/confirm-dialog-sp/confirm-dialog-sp.component';
import {
  isDuplicateSuiteSelected,
  duplicateSuite,
  NON_TRUSTED_USER_DATA,
} from './stacking-plan-container/floor-container/floor/item/details-modal/details-modal.constants';
import { remindTheirChoice } from './stacking-plan-container/dialogs/confirm-dialog-sp/confirm-dialog-sp.const';
import { EntityType } from '@miq/domain';

const NoAccessCIQ = 'You do not have access to opportunities. Please contact CIQ Admin';

@Injectable()
/**
 * Service class for managing stacking plan data.
 */
export class StackingPlanService extends HttpMethods {
  readonly #floorDataRepository = inject(FloorContainerRepository);
  readonly #localizationOptions = inject(LocalizationOptionsService);

  public floorElevatorInfo: BehaviorSubject<FloorsElevators> = new BehaviorSubject(null);
  public updateStackingPlanData: BehaviorSubject<UpdateStackingPlanInfo> = new BehaviorSubject(null);
  public spaceReplacement$: Observable<EfficiencyTransport>;
  public spaceCreation$: Observable<EfficiencyTransport>;
  public rentableSpace$: Observable<EfficiencyTransport>;
  public createNewAvailabilityTenancy: Subject<{
    remainingSpace: number;
    remainingSpaceUOM: string;
    floor: string;
    suiteNumber: string;
  }> = new Subject();
  public unaccountedSpaceTotal: number;
  public media: MediaInfo;
  public floors: EDPFloorSpaceInfo[] = [];
  private readonly spaceChanges: Subject<void | UpdateStackingPlanInfo> = new Subject();
  private readonly spaceReplacement: Subject<EfficiencyTransport> = new Subject();
  private readonly spaceCreation: Subject<EfficiencyTransport> = new Subject();
  private readonly rentableSpace: Subject<EfficiencyTransport> = new Subject();
  private _propertyId: number;
  private _propertyData: PropertyData;
  public readonly baseUrlLocal: string;
  private requestCache = {};
  private user: any;
  private usagesProperty: DataObject[] = [];
  private _ciqPath;
  private _pendingIssues: IdentifiedIssue[];
  /** per-load in-flight cache, keyed by `${propertyId}|${propertyType}`; warmed at load start (QW-04) */
  readonly #issues = new Map<string, Observable<IdentifiedIssue[]>>();
  /** per-load in-flight cache for the single `/stack/settings` read (QW-04) */
  #settings$: Observable<Settings> | null = null;
  /**
   * Whether the connected BE understands `include=filters,issues` (HV-11B), learned from the
   * first `/stack2/{id}` response of the session. `undefined` until then, so the very first
   * load never assumes support and the toolbars' own calls are what fetch issues/filters,
   * exactly today's (pre-HV-11F) behaviour. Once known, `false` restores QW-04's parallel
   * prefetch for every later load against that (old) BE.
   */
  #includeSupported: boolean | undefined;

  /** Whether the last `/stack2/{id}` response carried the `include` bundle (HV-11F). */
  public get includeSupported(): boolean | undefined {
    return this.#includeSupported;
  }
  readonly #lastReviewedData = new BehaviorSubject<StackingPlanReviewer>({ reviewedBy: '', reviewedOn: '' });
  lastReviewedData$ = this.#lastReviewedData.asObservable();

  readonly #containerData: BehaviorSubject<StackingPlanContainer> = new BehaviorSubject(null);
  public containerData$: Observable<StackingPlanContainer> = this.#containerData.asObservable();

  get containerDataSnap(): StackingPlanContainer {
    return this.#containerData.value;
  }

  setContainerData(data: StackingPlanContainer) {
    this.#floorDataRepository.set(data);
    this.#setUnaccountedSpace(this.#floorDataRepository.floorData);
    this.#containerData.next(data);
  }

  readonly #setUnaccountedSpace = (floorData: FloorClass[]): void => {
    const unaccountedSpaceTotal = floorData.reduce((sumUnaccountedSpace: number, spaceClass: FloorClass) => {
      const unaccountedSpace = spaceClass.floorArea - spaceClass.getFloorSpaceAreaSum();
      if (!spaceClass.hideFloor) {
        sumUnaccountedSpace += unaccountedSpace > 0 ? unaccountedSpace : 0;
      }
      return sumUnaccountedSpace;
    }, 0);
    this.unaccountedSpaceTotal = unaccountedSpaceTotal;
  };

  getFloorContainerRepository(): FloorContainerRepository {
    return this.#floorDataRepository;
  }

  get ciqPath() {
    return this._ciqPath;
  }

  get propertyId() {
    return this._propertyId;
  }
  get propertyData() {
    return this._propertyData;
  }

  set pendingIssues(value: IdentifiedIssue[]) {
    this._pendingIssues = value;
  }

  get pendingIssues(): IdentifiedIssue[] {
    return this._pendingIssues;
  }

  constructor(
    private readonly httpClient: HttpClient,
    private readonly cbreAlertService: CbreAlertService,
    @Inject(STACKING_PLAN_CONFIGS_TOKEN) spConfigs: StackingPlanConfiguration,
    private readonly filtersService: FiltersService,
    private readonly notificationService: NotificationService
  ) {
    super(spConfigs.baseUrl, httpClient);
    this.spaceReplacement$ = this.spaceReplacement.asObservable();
    this.spaceCreation$ = this.spaceCreation.asObservable();
    this.rentableSpace$ = this.rentableSpace.asObservable();
    this.baseUrlLocal = spConfigs.baseUrl;
    this.media = spConfigs.media;
  }

  public setUsagesProperty(_usagesProperty: DataObject[]) {
    this.usagesProperty = _usagesProperty;
  }
  /**
   * Used to initialize any value that must be shared across the stacking plans
   */
  public setPropertyId(propertyId: number) {
    this._propertyId = propertyId;
  }

  public initSetCiqPath(ciqPath) {
    this._ciqPath = ciqPath;
  }

  public getStackingPlan<T extends EDPStackingPlan | Building = EDPStackingPlan>(
    propertyId: number,
    options: { propertyType?: string | null } = {}
  ): Observable<{ results: T }> {
    const endpoint = GetEndPointSP(propertyId);
    const uri = `${this.baseUrlLocal}/${endpoint}`;

    // Capture the exact filterView/propertyType/localeId THIS request is sent with. The stack2
    // response can land long after the toolbar has moved the user to a different view/property
    // type/locale (its own getFilterData call updates FiltersService's lastView in ~250ms, well
    // before a ~2.4s stack2 response), so the seed below must use what this request actually
    // asked for, never whatever FiltersService/StackPlanViewStateService/localization show at
    // response time - reading "current" state there seeded the old response's filters under the
    // NEW (wrong) key (HV-11F review: stale-key cache poisoning).
    const filterView = this.filtersService.lastView;
    const propertyType = options.propertyType;
    const localeId = this.#localizationOptions.getInstance()?.localeId;

    // include=filters,issues (HV-11B contract, 06-architecture.md) lets one stack2 response also
    // carry the toolbars' data; harmless against an old BE, which ignores unknown params and
    // returns its unchanged response (no `filters`/`issues` keys).
    const params = {
      ...(propertyType != null ? { usageTypes: propertyType } : {}),
      include: 'filters,issues',
      filterView,
    };
    const requestOptions: any = { params, observe: 'response' as const };

    return this.httpClient
      .get<{
        results: T;
        filters?: ToolbarFilterOption[] | null;
        issues?: IdentifiedIssue[] | null;
      }>(uri, requestOptions)
      .pipe(
        tap(response =>
          this.#seedFromIncludeBundle(
            propertyId,
            filterView,
            propertyType,
            localeId,
            response as HttpResponse<{ filters?: ToolbarFilterOption[] | null; issues?: IdentifiedIssue[] | null }>
          )
        ),
        catchError(this.#handleStackCallError(propertyId, StackViewType.Default)),
        map(response => this.#toStackingPlanView(response as HttpResponse<{ results: EDPStackingPlan }>))
      );
  }

  /**
   * Reads the stack2 include bundle (HV-11F, contract in docs/perf-workflows/06-architecture.md
   * §HV-11) before `#toStackingPlanView` strips it out, and seeds the QW-04 per-load caches so
   * the issues/filters toolbars' own (unchanged) calls hit the cache instead of the network.
   * Runs only on a genuine response (`tap` before `catchError`), never on the synthetic
   * "no access"/error fallback body.
   *
   * `filterView`/`propertyType`/`localeId` are the values `getStackingPlan` captured when it
   * built THIS request, passed through unchanged - never re-read live here. See the stale-key
   * warning on `getStackingPlan` and on `FiltersService.seedFilterData`.
   */
  readonly #seedFromIncludeBundle = (
    propertyId: number,
    filterView: FilterView,
    propertyType: string | null | undefined,
    localeId: number | undefined,
    response: HttpResponse<{ filters?: ToolbarFilterOption[] | null; issues?: IdentifiedIssue[] | null }>
  ): void => {
    const body = response?.body;
    if (body == null) return;
    const hasFilters = 'filters' in body;
    const hasIssues = 'issues' in body;
    this.#includeSupported = hasFilters || hasIssues;

    if (hasFilters && body.filters !== null) {
      this.filtersService.seedFilterData(propertyId, filterView, propertyType, localeId, body.filters);
    }
    if (hasIssues && body.issues !== null) {
      this.#issues.set(this.#issuesKey(propertyId, propertyType), of(body.issues));
    }
  };

  public getPVStackPlan(propertyId: number, params: Record<string, any> = {}): Observable<{ results: Building }> {
    const apiVersion = isTenantEntityActive() ? 'stack' : 'stack3';
    const endpoint = `${apiVersion}/${propertyId}/pv`;
    const uri = `${this.baseUrlLocal}/${endpoint}`;
    return this.httpClient.get(uri, { observe: 'response', params }).pipe(
      catchError(this.#handleStackCallError(propertyId, StackViewType.PropertyView)),
      map(response => this.#toBuildingView(response as HttpResponse<{ results: Building }>))
    );
  }

  readonly #handleStackCallError = (propertyId: number, stackView: StackViewType) => () => {
    const errorTranslationKey =
      stackView === StackViewType.PropertyView
        ? 'errorMessages.initialLoadErrorPV'
        : 'errorMessages.initialLoadErrorSP';
    this.cbreAlertService.showInfoSnackBar_l10n(errorTranslationKey);
    const emptyStackData = {
      entity: {
        id: propertyId,
        entity_type: 'property',
        edp_last_update_date: '',
        edp_last_update_by: '',
        transaction_flag: '',
        edp_created_by: '',
        edp_created_date: '',
        edp_lineage: [],
        source_lineage: [],
      },
      property_name: '',
      ref_property_status_desc: '',
      year_property_built: 0,
      number_elevator_bank: 0,
      elevator_bank: [],
      ref_property_class_type_desc: '',
      total_number_of_floors: 0,
      number_of_floor_below_ground: 0,
      total_gross_area: 0,
      total_gross_area_uom_desc: '',
      net_rentable_area: 0,
      net_rentable_area_uom_desc: '',
      typical_floor_area: 0,
      typical_floor_area_uom_desc: '',
      minimum_floor_area: 0,
      minimum_floor_area_uom_desc: '',
      maximum_floor_area: 0,
      maximum_floor_area_uom_desc: '',
      colloquial: [],
      total_vacant_area: 0,
      total_vacant_area_uom_desc: '',
      total_available_surface_area: 0,
      total_available_surface_area_uom_desc: '',
      postal_address: undefined,
      property_owners: [],
      listing_reps: [],
      usages: [],
      floors: [],
      property_review_by: '',
      property_review_ts: '',
      dataStatus: SPDataStatus.ServerError,
    };
    return of({ body: { results: emptyStackData } });
  };

  readonly #toBuildingView = (response: HttpResponse<{ results: Building }>) => response?.body as { results: Building };

  readonly #toStackingPlanView = (response: HttpResponse<{ results: EDPStackingPlan }>) => {
    let result = { results: null };
    if (response.body?.results != null) {
      const { body: stackingPlan } = response;
      // EDP sends null spaces
      const floorsWProcessedSpaces = (stackingPlan.results.floors || []).map(floor => ({
        ...floor,
        spaces: SpaceClass.filterOutInvalidSpaces(floor.spaces),
      }));
      const spFloors = FloorClass.filterOutInvalidFloorsWithNoSpaces(floorsWProcessedSpaces);
      this.floors = spFloors;
      stackingPlan.results.floors = FloorClass.sortFloorsSpaces(spFloors);
      this._propertyData = {
        propertyName: stackingPlan.results.property_name,
        postalAddress: stackingPlan.results.postal_address,
      };
      result = stackingPlan;
    }
    return result;
  };

  issueToSpace(floorWithIssues: IdentifiedIssue): Partial<EDPSpaceInfo> {
    const { suite, id, recordType } = floorWithIssues;
    const type = recordType === ('Tenancy' as SuiteType) ? 'tenant' : recordType.toLocaleLowerCase();
    return {
      suite_number: suite.value as string,
      [type]: {
        id,
      },
    };
  }

  public replaceSpace(spaceToReplace?: EfficiencyTransport) {
    this.spaceReplacement.next(spaceToReplace);
  }

  public createSpace(spaceToCreate?: EfficiencyTransport) {
    this.spaceCreation.next(spaceToCreate);
  }

  public makeRentableSpace(spaceToReplace: EfficiencyTransport) {
    this.rentableSpace.next(spaceToReplace);
  }

  public setFloorElevatorInfo(floorElevatorInfo: FloorsElevators): void {
    this.floorElevatorInfo.next(floorElevatorInfo);
  }

  public get floorElevatorInfoSnapshot(): FloorsElevators {
    return this.floorElevatorInfo.value;
  }

  public setUpdatedStackingData(updateStackingInfo: UpdateStackingPlanInfo): void {
    this.updateStackingPlanData.next(updateStackingInfo);
  }

  public getUpdatedStackingData(): Observable<UpdateStackingPlanInfo> {
    return this.updateStackingPlanData.asObservable();
  }

  public setNewAvailabityTenancyCreation(createObj: {
    remainingSpace: number;
    remainingSpaceUOM: string;
    floor: string;
    suiteNumber: string;
  }): void {
    this.createNewAvailabilityTenancy.next(createObj);
  }

  public getNewAvailabityTenancyCreation(): Observable<{
    remainingSpace: number;
    remainingSpaceUOM: string;
    floor: string;
    suiteNumber: string;
  }> {
    return this.createNewAvailabilityTenancy.asObservable();
  }

  public setSpaceChanges() {
    this.spaceChanges.next();
  }

  public getSpaceChanges() {
    return this.spaceChanges.asObservable();
  }

  public getSettings(): Observable<Settings> {
    if (!this.#settings$) {
      const uri = `/stack/settings`;
      this.#settings$ = this.httpGet<Settings>(uri).pipe(
        catchError(err => {
          this.#settings$ = null;
          return throwError(() => err);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.#settings$;
  }

  public saveSettings(settings: Settings): Observable<Settings> {
    const uri = `/stack/settings`;
    return this.httpPut(uri, settings).pipe(tap(() => (this.#settings$ = null)));
  }

  /**
   * Clears the per-load in-flight caches (issues, filters) so a new stacking plan
   * load never reuses data from a previous property or a previous view of the
   * same property (QW-04).
   */
  public invalidateLoadCaches(): void {
    this.#issues.clear();
    this.filtersService.clearCache();
  }

  public resetSubjects() {
    this.#floorDataRepository.reset();
    this.setUpdatedStackingData(null);
  }

  public getUnitValues(): Observable<any> {
    // TODO: Need to use actual uri to get unit values and need to replace type any with actual type
    const uri = `stack/units`;
    return this.httpGet(uri);
  }

  public createNonRentableSpace(data): Observable<any> {
    // TODO: Need to use actual uri to create non rentable space and need to replace type any with actual type
    const uri = `stack/createspace`;
    return this.httpPost(uri, data);
  }

  public sendVerifiedEntity(entity: string, id: number): Observable<any> {
    return this.httpPut('/write/verify', { entity, id });
  }

  public getRecord(entity: string, id: number): Observable<any> {
    const payload = { id, entity, userId: this.resolveUser()?.id, view: 'card' };
    return this.httpPost(`/search/record`, payload);
  }

  public getInactiveRecordsByProperty(entity: string): Observable<{ totalCount: number; results: any[] }> {
    const payload = {
      entity,
      geography: {
        markets: [],
        propertyTypes: [],
        submarkets: [],
        districts: [],
        neighborhoods: [],
      },
      offset: 0,
      limit: 500,
      filters: [
        entity === EntityType.Availability
          ? {
              type: 'equal',
              field: 'ref_space_availability_status_desc',
              dataType: 'text',
              values: 'Off Market',
            }
          : {
              type: 'multi',
              field: 'ref_arrangement_status_desc',
              key: 1743,
              values: ['Inactive', '(empty)'],
              dataType: 'text',
              name: 'Comp Status',
            },
        {
          type: 'equal',
          field: 'property.id',
          values: this.propertyId,
          dataType: 'id',
          name: 'Property ID',
        },
      ],
      userId: this.resolveUser()?.id,
    };
    return this.httpPost(`/search?view=table&ignoreHistory=false`, payload);
  }

  /**
   * Get filter data
   */
  public getFilterData(
    view: FilterView,
    localeId?: number,
    isRemoveUnaccountedEnabled?: boolean
  ): Observable<ToolbarFilterOption[]> {
    const options: FilterDataRequest = { view, localeId, isRemoveUnaccountedEnabled };
    return this.filtersService.getFilterData(this.propertyId, options);
  }

  readonly #issuesKey = (propertyId: number, propertyType: string | null | undefined): string =>
    `${propertyId}|${propertyType ?? ''}`;

  getPendingIssuesData(
    options: { propertyType?: string },
    propertyId: number = this.propertyId
  ): Observable<IdentifiedIssue[]> {
    const key = this.#issuesKey(propertyId, options.propertyType);
    if (!this.#issues.has(key)) {
      const uri = `/${GetEndPointSP(propertyId)}/issues`;
      const params = options.propertyType != null ? { usageTypes: [options.propertyType] } : {};
      this.#issues.set(
        key,
        this.httpGet<IdentifiedIssue[]>(uri, new HttpParams({ fromObject: params })).pipe(
          catchError(err => {
            this.#issues.delete(key);
            return throwError(() => err);
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        )
      );
    }
    return this.#issues.get(key);
  }

  /**
   * Returns the navigations url for entity record details page, based on entity
   * and entity record id
   */
  public getNavigationURL(entity: string, recordID: any) {
    return `${'records/' + entity.toLowerCase() + '/' + recordID + '/edit'}`;
  }

  public getLovValues(uri: string): Observable<LovValues[]> {
    return this.cache(uri, this.httpGet.bind(this, uri));
  }

  public getLovValuesMap(uri: string): Observable<{ [uomKey: string]: string }> {
    return this.getLovValues(uri).pipe(
      map((uomLov: LovValues[]) => {
        return uomLov.reduce((acc, uomOption) => {
          const { key, value } = uomOption;
          return { ...acc, [key.toString()]: value };
        }, {});
      })
    );
  }

  public get toolbarData(): Toolbar {
    return this.getToolbarData(this.containerDataSnap);
  }

  public get floorContainerData(): FloorContainer {
    const {
      elevatorBank,
      floors,
      totalNumberOfFloors,
      totalNumberOfFloorsUnderground,
      is_selected_usage_primary: isSelectedUsagePrimary,
      has_usage_primary: hasUsagePrimary,
    } = this.containerDataSnap;
    return {
      elevatorBank,
      floors,
      totalNumberOfFloors,
      totalNumberOfFloorsUnderground,
      isSelectedUsagePrimary,
      hasUsagePrimary,
    };
  }

  private getToolbarData(containerData: StackingPlanContainer): Toolbar {
    return {
      floors: containerData.floors,
      totalNumberOfFloors: containerData.totalNumberOfFloors,
      netRentableArea: containerData.netRentableArea,
      netRentableAreaUOM: containerData.netRentableAreaUOM,
      totalVacancyArea: containerData.totalVacancyArea,
      totalVacancyAreaUOM: containerData.totalVacancyAreaUOM,
      totalGrossArea: containerData.totalGrossArea,
      totalGrossAreaUOM: containerData.totalGrossAreaUOM,
      totalGrossOfficeArea: containerData.totalGrossOfficeArea,
      totalGrossOfficeAreaUOM: containerData.totalGrossOfficeAreaUOM,
      unknownIssues: containerData.unknownIssues,
      expiredIssues: containerData.expiredIssues,
      underGroundFloors: containerData.totalNumberOfFloorsUnderground,
      commonFloorSize: containerData.commonFloorSize,
      commonFloorSizeUOM: containerData.commonFloorSizeUOM,
      minimumFloorArea: containerData.minimumFloorArea,
      minimumFloorAreaUOM: containerData.minimumFloorAreaUOM,
      maximumFloorArea: containerData.maximumFloorArea,
      maximumFloorAreaUOM: containerData.maximumFloorAreaUOM,
    };
  }

  public resolveUser(): IUser {
    this.user ??= JSON.parse(localStorage.getItem('userInfo'));
    return this.user ?? {};
  }

  public isTrustedUser(): boolean {
    const { groups } = this.resolveUser();
    return (groups ?? []).some((group: any) => group?.value == 'Trusted User');
  }

  public hasEDPWriteAccess(): boolean {
    const { groups } = this.resolveUser();
    return (groups ?? []).some((group: any) => group?.value == 'EDP Write Access');
  }

  public getPropertyUsage(propertyUsageTypeDesc: string): DataObject | null {
    return this.usagesProperty.find(
      ({ ref_property_usage_type_desc }) => ref_property_usage_type_desc === propertyUsageTypeDesc
    );
  }

  private cache(uri: string, request: () => Observable<LovValues[]>, refreshCache?: boolean) {
    if (this.requestCache[uri] && !refreshCache) {
      const isObservable = typeof this.requestCache[uri].subscribe === 'function';
      return isObservable ? this.requestCache[uri] : of(this.requestCache[uri]);
    }
    this.requestCache[uri] = request().pipe(
      tap(response => (this.requestCache[uri] = response)),
      share()
    );
    return this.requestCache[uri];
  }

  public getOpportunities(params: DataObject): Observable<OpportunitiesModel> {
    const { emailAddress } = this.resolveUser();
    params = { ...params, userid: emailAddress }; //emailAddress
    return this.loadGetData('/ciq/services/apexrest/v1.0/OpportunityService', { params }).pipe(
      map((response: DataObject | DataObject[]) => {
        const opportunities = Array.isArray(response) ? response : [];
        const message = !Array.isArray(response) ? response.text : '';
        const isAccessCIQ = message !== NoAccessCIQ;
        return {
          isAccessCIQ,
          opportunities,
          message,
        };
      })
    );
  }

  private loadGetData(uri: string, options?: DataObject): Observable<any> {
    return this.httpClient.get(uri, options).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 200) {
          return of(error.error);
        }
        return throwError(() => error.error);
      })
    );
  }

  searchCbreEmployees(query: string): Observable<IUser[]> {
    const params = new HttpParams({ fromObject: { query } });
    return this.httpGet(`/search/employees`, params).pipe(
      map(({ results }: Record<string, any>) => {
        const employees = results ?? [];
        const employeesWithEmail = employees.filter((employee: CBREEmployee) => employee.work_email?.trim());
        return cbreEmployeesToIUsers(employeesWithEmail);
      })
    );
  }

  public getLastReviewedData() {
    return this.lastReviewedData$;
  }

  updateLastReviewedData = (data: StackingPlanReviewer) => {
    this.#lastReviewedData.next(data);
  };

  updateLastReviewer(recordId: number, reviewerInfo: StackingPlanReviewer): Observable<StackingPlanReviewer> {
    const { reviewedBy, reviewedOn } = reviewerInfo;
    const payload = {
      'property.property_review_by': reviewedBy,
      'property.property_review_ts': reviewedOn,
      property_id: recordId,
      'request.source_id': recordId,
    };
    return this.httpPut(`/write?entity=property&id=${recordId}&method=updateProperty`, payload).pipe(
      map(() => reviewerInfo),
      tap(this.updateLastReviewedData)
    );
  }

  alertNonTrustedUser(): Observable<boolean> {
    if (!this.isTrustedUser()) {
      //Alert user if he's a non-trusted user
      const choice = remindTheirChoice(NON_TRUSTED_USER_DATA.rememberMyChoiceDialogId);
      if (choice !== null) {
        return of(choice);
      }
      return this.notificationService.openDialog(ConfirmDialogSPComponent, NON_TRUSTED_USER_DATA, {
        panelClass: 'alert-modals',
      });
    }
    return of(true);
  }

  alertDuplicateSuite(floor, selectedSuiteNumber, suiteType, suiteId): Observable<boolean> {
    const duplicateSuiteSelected = isDuplicateSuiteSelected(floor, selectedSuiteNumber, suiteType, suiteId);
    if (duplicateSuiteSelected) {
      return this.notificationService
        .openDialog(ConfirmDialogSPComponent, duplicateSuite(suiteType), { panelClass: 'alert-modals' })
        .pipe(take(1));
    }
    return of(true);
  }
}

function cbreEmployeesToIUsers(employees: CBREEmployee[]): IUser[] {
  return employees.map(cbreEmployee => {
    const { work_email, first_name, last_name, employee_id } = cbreEmployee;

    const emailAddress = (work_email || '').trim();
    const firstName = (first_name || '').trim();
    const lastName = (last_name || '').trim();

    return { id: Number(employee_id), emailAddress, firstName, lastName };
  });
}
